export const levenshteinDistance = (a: string, b: string): number => {
  if (a.length === 0) {
    return b.length;
  }
  if (b.length === 0) {
    return a.length;
  }

  const matrix: number[][] = Array(b.length + 1)
    .fill(null)
    .map(() => Array(a.length + 1).fill(0));

  for (let i = 0; i <= b.length; i++) {
    const row = matrix[i];
    if (row) {
      row[0] = i;
    }
  }

  for (let j = 0; j <= a.length; j++) {
    const row = matrix[0];
    if (row) {
      row[j] = j;
    }
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      const indicator = a[j - 1] === b[i - 1] ? 0 : 1;
      const currentRow = matrix[i];
      const prevRow = matrix[i - 1];

      if (currentRow && prevRow) {
        currentRow[j] = Math.min(
          (currentRow[j - 1] ?? 0) + 1, // deletion
          (prevRow[j] ?? 0) + 1, // insertion
          (prevRow[j - 1] ?? 0) + indicator // substitution
        );
      }
    }
  }

  const lastRow = matrix[b.length];
  return lastRow?.[a.length] ?? 0;
};

export const normalizeForMatch = (s: string): string => {
  let normalized = s.toLowerCase().replace(/[^a-z0-9]/g, '');
  // Remove 'insert' prefix
  normalized = normalized.replace(/^insert/, '');
  // Remove common technical suffixes
  normalized = normalized.replace(/(subdocs|list)$/, '');
  // Remove plural 's' (simple heuristic)
  if (normalized.endsWith('s') && !normalized.endsWith('ss')) {
    normalized = normalized.slice(0, -1);
  }
  return normalized.trim();
};

export const getTokens = (s: string): string[] => {
  // Keep alpha-numeric and spaces
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .split(/\s+/)
    .filter((t) => t.length > 2); // Filter out very short words
};

export const isFuzzyDuplicate = (title: string, otherTitle: string): boolean => {
  // 1. Check normalized Levenshtein distance
  const s1 = normalizeForMatch(title);
  const s2 = normalizeForMatch(otherTitle);

  if (s1 === s2) {
    return true; // Exact match after normalization
  }

  const maxLen = Math.max(s1.length, s2.length);
  if (maxLen > 3) {
    const dist = levenshteinDistance(s1, s2);
    const similarity = 1 - dist / maxLen;
    // Threshold 0.8 covers most typos and small variations
    if (similarity >= 0.8) {
      return true;
    }
  }

  // 2. Token/Substring Containment (for abbreviations like "Exec Summary" vs "Executive Summary")
  const t1 = getTokens(title);
  const t2 = getTokens(otherTitle);

  if (t1.length === 0 || t2.length === 0) {
    return false;
  }

  const set1 = new Set(t1);
  const set2 = new Set(t2);

  // Find intersection where tokens "match" (are contained in each other)
  // We want to see if the SMALLER set is fully covered by the LARGER set
  const [smallerSet, largerSet] = set1.size <= set2.size ? [set1, set2] : [set2, set1];

  let matchCount = 0;
  for (const smallToken of smallerSet) {
    // Check if smallToken matches any token in largerSet (fuzzy check: inclusion)
    const isContained = [...largerSet].some((largeToken) => {
      // Check for prefix/inclusion match
      // "exec" is in "executive"
      // "tech" is in "technical"
      return largeToken.includes(smallToken) || smallToken.includes(largeToken);
    });
    if (isContained) {
      matchCount++;
    }
  }

  // If all tokens in the smaller set are accounted for in the larger set
  // Stricter check: require 80% match against the LARGER set (or max size)
  // This prevents "Plan" being flagged as a duplicate of "Strategic Plan"
  const maxTokens = Math.max(set1.size, set2.size);
  return matchCount / maxTokens >= 0.8;
};
